import Styles from '../CalendarEventCard.module.scss'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { generatePath, Link } from 'react-router-dom'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import cx from 'classnames'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ArrowRefresh, Edit3, EyeOpen } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { PostPreviewModal } from 'components/PostPreviewModal/PostPreviewModal'
import { useModals } from 'almanac-ui/Modal'
import { PostableChannelType } from 'components/PostPreviewModal/Types'
import { useHover } from '@react-aria/interactions'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

interface HoverOverlayProps {
  event: AlmanacFileCalendarEvent
  isHovered: boolean
  isSelected?: boolean
  onSelect?: (id: string) => void
  isSelectView?: boolean
  onClick: () => void
  canSelect?: boolean
}

export const HoverOverlay = ({
  event,
  isHovered,
  isSelected,
  onSelect,
  isSelectView = false,
  onClick,
  canSelect = true,
}: HoverOverlayProps) => {
  const { scheduled_post_id, status, channel, asset_type, post_url, id, is_generating, image_url_generating } = event

  const { openModal } = useModals()
  const { isChannelSocialPostable } = useIsChannelPostable()
  const { hoverProps, isHovered: isHoveredOverlay } = useHover({})
  const isPostable = channel && isChannelSocialPostable(channel)
  const postIsCreating = !!event.scheduleContentId && !event.key
  const workspace = useCurrentWorkspace()
  const isFullScreenPreviewEnabled = useFlipperFlag('calendar_full_screen_preview')

  let to = event.id ? generatePath(DESIGN_PATH, { key: event.id }) : ''
  if (asset_type === 'Article') to = event.id ? generatePath(DOC_PATH, { slug: event.id }) : ''

  const openPostPreview = () => {
    if (!scheduled_post_id) return

    openModal(PostPreviewModal, {
      fileId: id,
      scheduledPostId: scheduled_post_id,
      channel: channel as PostableChannelType,
      showSuccessToast: false,
      pollPreviewContentStream: false,
      target: asset_type === 'Design' ? 'design' : 'text',
    })
  }

  const handleClick = () => {
    if (postIsCreating) return

    if (workspace?.is_virtual_marketer && isSelectView) {
      onSelect?.(event.id)
    } else if (isFullScreenPreviewEnabled) {
      onClick()
    }
  }

  const useLink = !isFullScreenPreviewEnabled && !isSelectView

  return (
    <div
      className={cx(Styles.hoverContainer, {
        [Styles.isHovered]: isHovered || isHoveredOverlay,
        [Styles.isSelected]: isSelected,
        [Styles.isSelectView]: isSelectView,
        [Styles.isLoading]: is_generating || image_url_generating,
        [Styles.postIsCreating]: postIsCreating,
      })}
      {...hoverProps}
    >
      {workspace?.is_virtual_marketer && !postIsCreating && canSelect && (
        <div className={Styles.checkboxContainer} onClick={() => onSelect?.(event.id)}>
          <Checkbox size={28} aria-label="Selected image" color="var(--selected-fill)" isSelected={isSelected} />
        </div>
      )}

      {useLink && <Link to={to} className={Styles.hoverLink} />}
      <div className={cx(Styles.hoverContainerWrapper, { [Styles.useLink]: useLink })} onClick={handleClick}>
        {!isFullScreenPreviewEnabled && (
          <div className={Styles.hoverActionsContainer}>
            {status === PostStatusPropertyValue.Posted && post_url && (
              <div className={Styles.hoverAction}>
                <ButtonLink
                  variant="secondary"
                  size="xs"
                  frontIcon={() => <EyeOpen size={16} />}
                  to={post_url}
                  className={Styles.editButton}
                >
                  See Post
                </ButtonLink>
              </div>
            )}
            {status === PostStatusPropertyValue.Failed && scheduled_post_id && isPostable && (
              <div className={Styles.hoverAction}>
                <Button
                  variant="secondary"
                  size="xs"
                  frontIcon={() => <ArrowRefresh size={16} />}
                  fullWidth
                  onPress={openPostPreview}
                  className={Styles.editButton}
                >
                  Retry
                </Button>
              </div>
            )}
            {status !== PostStatusPropertyValue.Failed && status !== PostStatusPropertyValue.Posted && (
              <div className={Styles.hoverAction}>
                <Button
                  variant="secondary"
                  size="xs"
                  frontIcon={() => <EyeOpen size={16} />}
                  fullWidth
                  aria-label="Preview"
                  onPress={openPostPreview}
                  isDisabled={!scheduled_post_id || !isPostable}
                  className={Styles.editButton}
                >
                  Preview
                </Button>
              </div>
            )}
            <div className={Styles.hoverAction}>
              <ButtonLink
                size="xs"
                fullWidth
                frontIcon={() => <Edit3 size={16} />}
                to={to}
                className={Styles.editButton}
              >
                Edit
              </ButtonLink>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
